class PredictiveSearch extends HTMLElement {
    constructor() {
        super();
        this.cachedResults = {};
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector(
            '[data-predictive-search]'
        );
        this.isOpen = false;
        this.isHeaderSearch = this.hasAttribute('data-header-search');
        this.inputClearButton = this.querySelector('[input-clear-button]');
        this.searchProductTypes = this.querySelector('[data-search-product-types]');
        this.onClickOutside = this.handleClickOutside.bind(this);
        this.onPanelReposition = this.positionResultsPanel.bind(this);

        document.addEventListener(_EVENT_HELPER.hideDrawer, (e) => {
            if (e.detail.drawerType == 'search-drawer') {
                this.close(true);
            }
        });

        if (this.input) {
            this.setupEventListeners();
        }
    }

    setupEventListeners() {
        const form = this.querySelector('form.search');
        if (form) {
            form.addEventListener('submit', this.onFormSubmit.bind(this));
        }

        this.input.addEventListener(
            'input',
            HELPER_UTIL.debounce((event) => {
                this.onChange(event);
            }, 300).bind(this)
        );
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('keyup', this.onKeyup.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.inputClearButton &&
            this.inputClearButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.inputClear();
            });
        this.searchProductTypes?.addEventListener(
            'change',
            this.handleProductTypeChange.bind(this)
        );
        this.predictiveSearchResults?.addEventListener('click', (event) => {
            const submitBtn = event.target.closest('[data-header-search-submit]');
            if (!submitBtn) return;
            event.preventDefault();
            this.querySelector('form.search')?.requestSubmit();
        });
    }

    getQuery() {
        return this.input.value.trim();
    }

    getSearchTerm() {
        const query = this.getQuery();
        const productType = this.searchProductTypes?.value?.trim();
        if (productType && query) {
            return `product_type:${productType} AND ${query}`;
        }
        if (productType) {
            return `product_type:${productType}`;
        }
        return query;
    }

    handleProductTypeChange() {
        const query = this.getQuery();
        if (query.length > 0) {
            this.getSearchResults(query);
            return;
        }
        if (this.searchProductTypes?.value) {
            this.getSearchResults('');
        } else {
            this.close(true);
        }
    }

    syncClearButton() {
        if (!this.inputClearButton) return;
        if (this.getQuery().length) {
            this.inputClearButton.removeAttribute('hidden');
        } else {
            this.inputClearButton.setAttribute('hidden', '');
        }
    }

    onChange() {
        const searchTerm = this.getQuery();
        this.syncClearButton();

        if (!searchTerm.length && !this.searchProductTypes?.value) {
            this.close(true);
            return;
        }

        this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
        if (this.querySelector('[aria-selected="true"] a')) {
            event.preventDefault();
            return;
        }

        const query = this.getQuery();
        if (!query.length && !this.searchProductTypes?.value) {
            event.preventDefault();
            return;
        }

        this.input.value = this.getSearchTerm();
    }

    onFocus() {
        const searchTerm = this.getQuery();
        this.syncClearButton();

        if (!searchTerm.length) return;

        if (this.getAttribute('results') === 'true') {
            this.open();
        } else {
            this.getSearchResults(searchTerm);
        }
    }

    bindOutsideClick() {
        if (!this.isHeaderSearch) return;
        document.removeEventListener('click', this.onClickOutside);
        setTimeout(() => {
            document.addEventListener('click', this.onClickOutside);
        }, 0);
    }

    unbindOutsideClick() {
        document.removeEventListener('click', this.onClickOutside);
        window.removeEventListener('resize', this.onPanelReposition);
    }

    handleClickOutside(event) {
        if (!this.isHeaderSearch) return;
        if (this.contains(event.target)) return;
        if (event.target?.closest?.('[data-search-product-types]')) return;
        this.close(false);
    }

    onKeyup(event) {
        if (!this.getQuery().length && !this.searchProductTypes?.value) {
            this.close(true);
        }
        event.preventDefault();

        switch (event.code) {
            case 'ArrowUp':
                this.switchOption('up');
                break;
            case 'ArrowDown':
                this.switchOption('down');
                break;
            case 'Enter':
                this.selectOption();
                break;
            case 'Escape':
                this.close(false);
                if (this.isHeaderSearch) {
                    this.input.blur();
                }
                break;
        }
    }

    onKeydown(event) {
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
            event.preventDefault();
        }
    }

    switchOption(direction) {
        if (!this.getAttribute('open')) return;

        const moveUp = direction === 'up';
        const selectedElement = this.querySelector('[aria-selected="true"]');
        const allElements = this.querySelectorAll(
            '.predictive-search__list.active .predictive-search__list-item'
        );
        let activeElement = this.querySelector(
            '.predictive-search__list.active .predictive-search__list-item'
        );
        if (moveUp && !selectedElement) return;

        if (!moveUp && selectedElement) {
            activeElement =
                selectedElement.nextElementSibling || allElements[0];
        } else if (moveUp) {
            activeElement =
                selectedElement.previousElementSibling ||
                allElements[allElements.length - 1];
        }

        if (!activeElement || activeElement === selectedElement) return;

        activeElement.setAttribute('aria-selected', true);
        if (selectedElement)
            selectedElement.setAttribute('aria-selected', false);

        this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
        const selectedProduct = this.querySelector(
            '[aria-selected="true"] a, [aria-selected="true"] button'
        );

        if (selectedProduct) selectedProduct.click();
    }

    getSearchResults(searchTerm) {
        const apiTerm = this.getSearchTerm();
        if (!apiTerm) {
            this.close(true);
            return;
        }

        const productType = this.searchProductTypes?.value || '';
        const sectionId = this.isHeaderSearch
            ? 'predictive-search-header'
            : 'predictive-search';
        const queryKey = `${sectionId}::${productType}::${searchTerm}`
            .replace(/\s+/g, '-')
            .toLowerCase();

        if (this.cachedResults[queryKey]) {
            this.renderSearchResults(this.cachedResults[queryKey]);
            return;
        }

        const searchUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(
            apiTerm
        )}&section_id=${sectionId}&resources[limit]=8&resources[limit_scope]=each`;

        fetch(searchUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.status);
                }
                return response.text();
            })
            .then((text) => {
                if (this.getSearchTerm() !== apiTerm) return;

                const sectionEl = new DOMParser()
                    .parseFromString(text, 'text/html')
                    .querySelector(`#shopify-section-${sectionId}`);

                if (!sectionEl) {
                    throw new Error('Predictive search section missing');
                }

                const resultsMarkup = sectionEl.innerHTML.trim();
                this.cachedResults[queryKey] = resultsMarkup;
                this.renderSearchResults(
                    resultsMarkup ||
                        `<div class="header-search-panel__empty"><p class="text-center">${
                            this.dataset.emptyText || 'No results found'
                        }</p></div>`
                );
            })
            .catch((error) => {
                console.error('Predictive search failed:', error);
                this.close(false);
            });
    }

    renderSearchResults(resultsMarkup) {
        if (!this.predictiveSearchResults) return;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute('results', 'true');
        this.activateTab();
        this.open();
    }

    activateTab() {
        this.predictiveSearchResults
            ?.querySelector('[tab-head]')
            ?.classList.add('active');
        this.predictiveSearchResults
            ?.querySelector('[tab-body]')
            ?.classList.add('active');
    }

    positionResultsPanel() {
        if (!this.isHeaderSearch || !this.predictiveSearchResults) return;
        const header =
            this.closest('header-component') ||
            document.querySelector('header-component');
        const top = header
            ? Math.round(header.getBoundingClientRect().bottom)
            : 0;
        document.documentElement.style.setProperty(
            '--header-search-panel-top',
            `${top}px`
        );
        this.predictiveSearchResults.style.top = `${top}px`;
        this.predictiveSearchResults.style.height = `calc(100vh - ${top}px)`;
        this.predictiveSearchResults.style.maxHeight = `calc(100vh - ${top}px)`;
        this.predictiveSearchResults.style.overflowY = 'auto';
    }

    open() {
        this.setAttribute('open', 'true');
        this.input.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
        if (this.isHeaderSearch) {
            this.positionResultsPanel();
            document.body.classList.add('header-search-open');
            this.bindOutsideClick();
            window.addEventListener('resize', this.onPanelReposition);
        }
    }

    close(clearSearchTerm = false) {
        if (clearSearchTerm) {
            this.input.value = '';
            this.removeAttribute('results');
            if (this.predictiveSearchResults) {
                this.predictiveSearchResults.innerHTML = '';
            }
        }

        const selected = this.querySelector('[aria-selected="true"]');

        if (selected) selected.setAttribute('aria-selected', false);

        this.input.setAttribute('aria-activedescendant', '');
        this.removeAttribute('open');
        this.input.setAttribute('aria-expanded', 'false');
        if (this.predictiveSearchResults) {
            this.predictiveSearchResults.removeAttribute('style');
        }

        this.isOpen = false;
        this.syncClearButton();

        if (this.isHeaderSearch) {
            document.body.classList.remove('header-search-open');
            document.documentElement.style.removeProperty(
                '--header-search-panel-top'
            );
            this.unbindOutsideClick();
        }
    }

    inputClear() {
        this.close(true);
        if (this.isHeaderSearch) {
            this.input.focus();
        }
    }
}

customElements.get('predictive-search') ||
    customElements.define('predictive-search', PredictiveSearch);

class PromptElement extends HTMLElement {
    constructor() {
        super();
        this.prompt_container = this.querySelector('[prompt-element]');
        this.init();
    }

    init() {
        if (!this.prompt_container) return;
        this.prompt_container.style.cssText =
            '--prompt-count-steps:steps(' +
            this.prompt_container.childElementCount +
            ');' +
            '--prompt-top:' +
            -this.prompt_container.childElementCount * 24 +
            'px;' +
            '--prompt-duration:' +
            this.prompt_container.childElementCount * 3 +
            's';
    }
}

customElements.get('prompt-element') ||
    customElements.define('prompt-element', PromptElement);
